import { useEffect, useState } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Loader2 } from 'lucide-react';
import 'katex/dist/katex.min.css';

/**
 * 助手消息富文本渲染（react-markdown 管线）：
 * - GFM：表格 / 删除线 / 任务列表；
 * - 公式：remark-math + rehype-katex（行内 $...$ 与块级 $$...$$，KaTeX 样式表随组件引入）；
 * - ```mermaid 代码块：动态 import mermaid 懒渲染为 SVG，渲染失败回退为代码块 + 错误提示
 *   （绝不让整条消息崩掉）；
 * - 其余围栏代码块：语言标签 + 复制按钮；行内代码保持 chip 样式。
 * 用户消息不走这里（保持纯文本）。
 */

function CodeBlock({ lang, code }: { lang: string; code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="md-codeblock">
      <div className="md-codeblock__head">
        <span className="md-codeblock__lang">{lang}</span>
        <button
          type="button"
          className="md-codeblock__copy"
          onClick={() => {
            void navigator.clipboard?.writeText(code).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? '已复制 ✓' : '复制'}
        </button>
      </div>
      <pre className="md-codeblock__body">
        <code>{code}</code>
      </pre>
    </div>
  );
}

let mermaidSeq = 0;

function MermaidBlock({ code }: { code: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 懒渲染：组件挂载后才动态加载 mermaid（不进主 bundle）；失败回退代码块
  useEffect(() => {
    let cancelled = false;
    const id = `pg-mmd-${++mermaidSeq}`;
    import('mermaid')
      .then(async (mod) => {
        const mermaid = mod.default;
        mermaid.initialize({
          startOnLoad: false,
          theme: document.documentElement.dataset.theme === 'light' ? 'default' : 'dark',
        });
        const result = await mermaid.render(id, code);
        if (!cancelled) setSvg(result.svg);
      })
      .catch((err: unknown) => {
        // mermaid 解析失败时可能向 body 注入错误图形容器，清理掉
        document.getElementById(`d${id}`)?.remove();
        document.getElementById(id)?.remove();
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (error) {
    return (
      <div className="md-mermaid-fallback">
        <div className="md-mermaid-fallback__note">图表渲染失败（{error}），已回退为源码：</div>
        <CodeBlock lang="mermaid" code={code} />
      </div>
    );
  }
  if (!svg) {
    return (
      <div className="md-mermaid md-mermaid--loading">
        <Loader2 size={14} className="spin" />
        图表渲染中…
      </div>
    );
  }
  // mermaid.render 输出为其生成的可信 SVG
  return <div className="md-mermaid" dangerouslySetInnerHTML={{ __html: svg }} />;
}

const components: Components = {
  // 去掉 <pre> 包裹，块级渲染完全由 code 分支接管（CodeBlock 自带 pre）
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const codeStr = String(children ?? '').replace(/\n$/, '');
    const match = /language-(\w+)/.exec(className ?? '');
    // 无语言标注且单行 → 行内代码；围栏代码块（含无语言标注）走 CodeBlock
    const isBlock = !!match || codeStr.includes('\n');
    if (!isBlock) return <code>{codeStr}</code>;
    const lang = match?.[1] ?? 'text';
    if (lang === 'mermaid') return <MermaidBlock code={codeStr} />;
    return <CodeBlock lang={lang} code={codeStr} />;
  },
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  // 套一层横向滚动容器，沿用既有表格样式
  table: ({ children }) => (
    <div className="md-table-wrap">
      <table>{children}</table>
    </div>
  ),
};

export default function Markdown({ content }: { content: string }) {
  return (
    <div className="md">
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
